/**
 * Telegram WebApp utilities with version compatibility
 * Handles HapticFeedback safely across different Telegram versions
 */

// Types for Telegram WebApp API
interface TelegramWebApp {
  version?: string;
  HapticFeedback?: {
    impactOccurred?: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
    notificationOccurred?: (type: 'error' | 'success' | 'warning') => void;
    selectionChanged?: () => void;
  };
}

declare global {
  interface Window {
    Telegram?: {
      WebApp?: TelegramWebApp;
    };
  }
}

/**
 * Check if HapticFeedback is supported in current Telegram version
 * @returns boolean indicating if HapticFeedback is available
 */
export const isHapticFeedbackSupported = (): boolean => {
  try {
    const telegram = window.Telegram?.WebApp;
    if (!telegram) return false;
    
    // Check if HapticFeedback object exists
    if (!telegram.HapticFeedback) return false;
    
    // Check version - HapticFeedback was introduced in version 6.1
    const version = telegram.version;
    if (!version) return false;
    
    // Parse version string (e.g., "6.1" -> [6, 1])
    const versionParts = version.split('.').map(Number);
    const majorVersion = versionParts[0] || 0;
    const minorVersion = versionParts[1] || 0;
    
    // HapticFeedback requires version 6.1 or higher
    return majorVersion > 6 || (majorVersion === 6 && minorVersion >= 1);
  } catch (error) {
    console.warn('⚠️ Failed to check HapticFeedback support:', error);
    return false;
  }
};

/**
 * Safe HapticFeedback impact with version check
 * @param style Impact style
 */
export const safeHapticImpact = (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft' = 'light'): void => {
  try {
    if (!isHapticFeedbackSupported()) {
      if (import.meta.env.DEV) {
        console.log(`🔇 HapticFeedback.impactOccurred(${style}) - Not supported in current version`);
      }
      return;
    }
    
    const haptic = window.Telegram?.WebApp?.HapticFeedback;
    if (haptic?.impactOccurred) {
      haptic.impactOccurred(style);
      if (import.meta.env.DEV) {
        console.log(`📳 HapticFeedback.impactOccurred(${style}) - Success`);
      }
    }
  } catch (error) {
    console.warn(`⚠️ HapticFeedback.impactOccurred(${style}) failed:`, error);
  }
};

/**
 * Safe HapticFeedback notification with version check
 * @param type Notification type
 */
export const safeHapticNotification = (type: 'error' | 'success' | 'warning'): void => {
  try {
    if (!isHapticFeedbackSupported()) {
      if (import.meta.env.DEV) {
        console.log(`🔇 HapticFeedback.notificationOccurred(${type}) - Not supported in current version`);
      }
      return;
    }
    
    const haptic = window.Telegram?.WebApp?.HapticFeedback;
    if (haptic?.notificationOccurred) {
      haptic.notificationOccurred(type);
      if (import.meta.env.DEV) {
        console.log(`📳 HapticFeedback.notificationOccurred(${type}) - Success`);
      }
    }
  } catch (error) {
    console.warn(`⚠️ HapticFeedback.notificationOccurred(${type}) failed:`, error);
  }
};

/**
 * Safe HapticFeedback selection changed with version check
 */
export const safeHapticSelectionChanged = (): void => {
  try {
    if (!isHapticFeedbackSupported()) {
      if (import.meta.env.DEV) {
        console.log('🔇 HapticFeedback.selectionChanged() - Not supported in current version');
      }
      return;
    }
    
    const haptic = window.Telegram?.WebApp?.HapticFeedback;
    if (haptic?.selectionChanged) {
      haptic.selectionChanged();
      if (import.meta.env.DEV) {
        console.log('📳 HapticFeedback.selectionChanged() - Success');
      }
    }
  } catch (error) {
    console.warn('⚠️ HapticFeedback.selectionChanged() failed:', error);
  }
};

/**
 * Get Telegram WebApp version info for debugging
 * @returns Version information object
 */
export const getTelegramInfo = () => {
  try {
    const telegram = window.Telegram?.WebApp;
    return {
      isAvailable: !!telegram,
      version: telegram?.version || 'unknown',
      hasHapticFeedback: !!telegram?.HapticFeedback,
      isHapticSupported: isHapticFeedbackSupported(),
      methods: {
        impactOccurred: !!telegram?.HapticFeedback?.impactOccurred,
        notificationOccurred: !!telegram?.HapticFeedback?.notificationOccurred,
        selectionChanged: !!telegram?.HapticFeedback?.selectionChanged
      }
    };
  } catch (error) {
    return {
      isAvailable: false,
      version: 'error',
      hasHapticFeedback: false,
      isHapticSupported: false,
      error: String(error)
    };
  }
};